import { useState, useEffect } from "react";
import {
  useNavigate,
  NavigateFunction,
  Link,
  useSearchParams,
} from "react-router-dom";
import { Input, Button, message, Form, Row, Col } from "antd";
import { MobileOutlined } from "@ant-design/icons";
import { noop, PHONE_RX } from "../../utils";
import { headers } from "../../request";
import Icon from "../../components/Icon";
import useCountDown from "../../hook/useCountDown";
import md5 from "../../md5";
import {
  getCode,
  register,
  passwordLogin,
  codeLogin,
  resetLogin,
} from "../../request/login";
import styles from "./index.module.css";

const bindInviteCode = (path: string, code: string | null) => {
  if (code) {
    return path + "?code=" + code;
  }
  return path;
};

const handleLogin = (navigate: NavigateFunction, res: any) => {
  if (res.Token) {
    localStorage.setItem("token", JSON.stringify(res.Token));
    if (res.Token.access_token) {
      headers.Authorization = "Bearer " + res.Token.access_token;
    }
  } else {
    message.error("获取登录信息失败");
    return;
  }
  if (res.User) {
    localStorage.setItem("user", JSON.stringify(res.User));
  } else {
    message.error("获取用户信息失败");
    return;
  }
  navigate("/home/gpu", { replace: true });
};

const enum LOGIN_TYPE {
  PASSWORD,
  SHORT,
  FORGET,
}

const createCountKey = (isRegister: boolean, loginType: LOGIN_TYPE) => {
  if (isRegister) {
    return "login-register-count";
  }
  if (loginType === LOGIN_TYPE.SHORT) {
    return "login-short-count";
  }

  if (loginType === LOGIN_TYPE.FORGET) {
    return "login-forget-count";
  }
  return undefined;
};

const Login = (props: { status: "login" | "register" }) => {
  const { status } = props;
  const isRegister = status === "register";
  const [remain, countHandle] = useCountDown();
  const [form] = Form.useForm();
  const phone = Form.useWatch("phone", form);
  const [loginType, setLoginType] = useState<LOGIN_TYPE>(LOGIN_TYPE.PASSWORD);
  const [loginOrRegister, setLoginOrRegister] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get("code");
  useEffect(() => {
    form.resetFields();
    const countKey = createCountKey(isRegister, loginType);
    if (countKey) {
      countHandle.restart(countKey);
    }
  }, [form, isRegister, loginType, countHandle]);
  return (
    <div className={styles.login}>
      <div className={styles.login_bg} />
      <div className={styles.plane}>
        <div className={styles.welcome}>
          {isRegister
            ? "欢迎加入！👋"
            : loginType === LOGIN_TYPE.FORGET
            ? "重置密码"
            : "欢迎回来！👋"}
        </div>
        <div className={styles.login_regular}>
          {isRegister
            ? "注册您的账户，获得最新优惠！"
            : "登录您的账户，获得最新优惠！"}
        </div>
        <Form
          className={styles.form}
          form={form}
          onFinish={(values) => {
            if (isRegister && !values.checked) {
              message.warning("请勾选注册协议");
              return;
            }
            setLoginOrRegister(true);
            const handleLogin$ = handleLogin.bind(null, navigate);
            if (isRegister) {
              // 注册
              const registerData: {
                pass_hash: string;
                phone: string;
                phone_area: string;
                code: string;
                invite_code?: string;
              } = {
                pass_hash: md5(values.password),
                phone: values.phone,
                code: values.verify,
                phone_area: "+86",
              };
              const invite_code = inviteCode || values.inviteCode;
              if (invite_code) {
                registerData.invite_code = invite_code;
              }
              register(registerData)
                .finally(() => {
                  setLoginOrRegister(false);
                })
                .then(handleLogin$)
                .catch(noop);
            } else {
              if (loginType === LOGIN_TYPE.PASSWORD) {
                // 密码登录
                passwordLogin({
                  pass_hash: md5(values.password),
                  phone: values.phone,
                  phone_area: "+86",
                })
                  .finally(() => {
                    setLoginOrRegister(false);
                  })
                  .then(handleLogin$)
                  .catch(noop);
              }
              if (loginType === LOGIN_TYPE.SHORT) {
                // 验证码登录
                codeLogin({
                  phone: values.phone,
                  code: values.verify,
                  phone_area: "+86",
                })
                  .finally(() => {
                    setLoginOrRegister(false);
                  })
                  .then(handleLogin$)
                  .catch(noop);
              }

              if (loginType === LOGIN_TYPE.FORGET) {
                // 忘记密码
                resetLogin({
                  pass_hash: md5(values.password),
                  phone: values.phone,
                  code: values.verify,
                  phone_area: "+86",
                })
                  .finally(() => {
                    setLoginOrRegister(false);
                  })
                  .then((res) => {
                    message.success("密码修改成功");
                    if (res) {
                      handleLogin(navigate, res);
                    } else {
                      setLoginType(LOGIN_TYPE.PASSWORD);
                    }
                  })
                  .catch(noop);
              }
            }
          }}
        >
          <div className={styles.form_label}>账户名</div>
          <Form.Item
            name="phone"
            rules={[
              { required: true, message: "填写手机号" },
              { pattern: PHONE_RX, message: "手机号格式错误" },
            ]}
          >
            <Input
              variant="filled"
              className={styles.custom_input}
              prefix={<MobileOutlined style={{ fontSize: 22 }} />}
              placeholder="请输入手机号"
              size="large"
            />
          </Form.Item>
          {(isRegister ||
            loginType === LOGIN_TYPE.SHORT ||
            loginType === LOGIN_TYPE.FORGET) && (
            <>
              <div className={styles.form_label}>验证码</div>
              <Form.Item>
                <Row gutter={8} justify="space-between">
                  <Col span={verifyLoading || remain ? 18 : 18}>
                    <Form.Item
                      noStyle
                      name="verify"
                      rules={[
                        { required: true, message: "必须填写验证码" },
                        { pattern: /^\d{6}$/, message: "验证码格式错误" },
                      ]}
                    >
                      <Input
                        variant="filled"
                        className={styles.custom_input}
                        size="large"
                        placeholder="请输入验证码"
                      />
                    </Form.Item>
                  </Col>
                  <Col
                    span={verifyLoading || remain ? 6 : 6}
                    style={{ textAlign: "right" }}
                  >
                    <Button
                      block
                      size="large"
                      type="primary"
                      style={{ height: 54 }}
                      loading={verifyLoading}
                      disabled={
                        phone ? !PHONE_RX.test(phone) || !!remain : true
                      }
                      onClick={() => {
                        setVerifyLoading(true);
                        getCode({
                          phone_area: "+86",
                          phone: phone,
                        })
                          .finally(() => {
                            setVerifyLoading(false);
                          })
                          .then(() => {
                            countHandle.start(
                              60,
                              createCountKey(isRegister, loginType)
                            );
                          })
                          .catch(noop);
                      }}
                    >
                      {remain ? `${remain}s 后获取验证码` : "获取验证码"}
                    </Button>
                  </Col>
                </Row>
              </Form.Item>
            </>
          )}
          {(isRegister || loginType !== LOGIN_TYPE.SHORT) && (
            <>
              <div className={styles.form_label}>
                {loginType === LOGIN_TYPE.FORGET ? "新密码" : "密码"}
              </div>
              <Form.Item
                name="password"
                rules={[
                  { required: true, message: "必须填写密码" },
                  () => ({
                    validator(_, value) {
                      if (!value) {
                        return Promise.resolve();
                      }
                      if (value.length < 8 || value.length > 16) {
                        return Promise.reject(
                          new Error("密码长度在8-16位字符之间")
                        );
                      }
                      let hasDigit = false;
                      let hasAlpha = false;
                      for (let i = 0; i < value.length; i++) {
                        const n = value[i].charCodeAt();
                        if (n >= 48 && n <= 57) {
                          hasDigit = true;
                          continue;
                        }
                        if ((n >= 65 && n <= 90) || (n >= 97 && n <= 122)) {
                          hasAlpha = true;
                          continue;
                        }
                      }
                      if (!hasAlpha || !hasDigit) {
                        return Promise.reject(new Error("必须包含数字和字母"));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input.Password
                  size="large"
                  variant="filled"
                  className={styles.custom_input}
                  prefix={<Icon name="lock" />}
                  placeholder="请输入8～16位包含数字和字母组合"
                />
              </Form.Item>
            </>
          )}
          {isRegister && !inviteCode && (
            <>
              <div className={styles.form_label}>邀请码</div>
              <Form.Item name="inviteCode">
                <Input
                  variant="filled"
                  className={styles.custom_input}
                  size="large"
                  placeholder="请输入邀请码(选填)"
                />
              </Form.Item>
            </>
          )}
          {isRegister && (
            <div className={styles.policy}>
              <Form.Item name="checked" noStyle>
                <Checkbox />
              </Form.Item>
              <span
                style={{
                  color: "#1B2124",
                  marginLeft: 16,
                  fontSize: 16,
                }}
              >
                我已阅读并同意
              </span>
              <Link
                target="_blank"
                to="/docs/agreement"
                style={{ fontSize: 16 }}
              >
                《MyJuhe服务协议》
              </Link>
              <span
                style={{
                  color: "rgba(0, 0, 0, 0.45)",
                  marginLeft: 1,
                  fontSize: 16,
                }}
              >
                、
              </span>
              <Link
                target="_blank"
                to="/docs/agreement/policy"
                style={{ fontSize: 16 }}
              >
                《隐私协议》
              </Link>
            </div>
          )}
          {!isRegister && (
            <div style={{ marginBottom: 50 }}>
              {loginType === LOGIN_TYPE.PASSWORD && (
                <Button
                  type="link"
                  size="small"
                  onClick={() => setLoginType(LOGIN_TYPE.SHORT)}
                >
                  短信登录
                </Button>
              )}
              {loginType === LOGIN_TYPE.SHORT && (
                <Button
                  type="link"
                  size="small"
                  onClick={() => setLoginType(LOGIN_TYPE.PASSWORD)}
                >
                  密码登录
                </Button>
              )}
              {loginType === LOGIN_TYPE.PASSWORD && (
                <Button
                  type="link"
                  size="small"
                  style={{ float: "right", color: "#7F879E" }}
                  onClick={() => setLoginType(LOGIN_TYPE.FORGET)}
                >
                  忘记密码?
                </Button>
              )}
              {loginType === LOGIN_TYPE.FORGET && (
                <Button
                  type="link"
                  size="small"
                  onClick={() => setLoginType(LOGIN_TYPE.PASSWORD)}
                >
                  返回登录
                </Button>
              )}
            </div>
          )}
          <Form.Item>
            <Button
              block
              size="large"
              type="primary"
              loading={loginOrRegister}
              style={{ height: 54 }}
              htmlType="submit"
            >
              {isRegister
                ? "立即注册"
                : loginType === LOGIN_TYPE.FORGET
                ? "重置密码"
                : "登录"}
            </Button>
          </Form.Item>
          {loginType !== LOGIN_TYPE.FORGET && (
            <>
              <span className={styles.login_regular}>
                {isRegister ? "已有账号?" : "没有账号?"}
              </span>
              <Button
                size="small"
                type="link"
                onClick={() => {
                  navigate(
                    bindInviteCode(
                      isRegister ? "/login" : "/register",
                      inviteCode
                    )
                  );
                }}
              >
                {isRegister ? "立即登录" : "立即注册"}
              </Button>
            </>
          )}
        </Form>
      </div>
    </div>
  );
};

const Checkbox = (props: {
  value?: boolean;
  onChange?: (value: boolean) => void;
}) => {
  const { value, onChange } = props;
  return (
    <Icon
      name={value ? "checkCircleOutlined" : "checkCircle"}
      style={{ cursor: "pointer" }}
      onClick={() => {
        if (onChange) {
          onChange(!value);
        }
      }}
    />
  );
};

export default Login;
